import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import '../assets/ProfitSharingModal.css';

const ProfitSharingModal = ({ isOpen, onClose, profitSharers, mode = 'monthly', initialMonth, initialYear }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [selectedMonth, setSelectedMonth] = useState(initialMonth || new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(initialYear || new Date().getFullYear());
  const [percentages, setPercentages] = useState({}); // Simple object: { userId: percentage }
  const [usingDefault, setUsingDefault] = useState(true);
  const [hasMonthlyOverride, setHasMonthlyOverride] = useState(false);

  // Update month/year when props change
  useEffect(() => {
    if (initialMonth) setSelectedMonth(initialMonth);
    if (initialYear) setSelectedYear(initialYear);
  }, [initialMonth, initialYear]);

  useEffect(() => {
    if (isOpen && profitSharers && profitSharers.length > 0) {
      loadData();
    }
  }, [isOpen, selectedMonth, selectedYear, mode, profitSharers]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (mode === 'admin') {
        // Load default allocation
        await loadDefaultAllocation();
      } else {
        // Load monthly allocation (or default if no override)
        await loadMonthlyAllocation();
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load profit sharing data');
      // Initialize with equal split as fallback
      initializeEqualSplit();
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultAllocation = async () => {
    try {
      const response = await adminApi.getDefaultProfitShareAllocations();
      console.log('Default allocation response:', response.data);
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const newPercentages = {};
        response.data.data.forEach(alloc => {
          const userId = alloc.profitSharer?._id || alloc.profitSharer;
          newPercentages[userId] = alloc.percentage;
        });
        setPercentages(newPercentages);
      } else {
        initializeEqualSplit();
      }
    } catch (error) {
      console.error('Error loading default:', error);
      initializeEqualSplit();
    }
  };

  const loadMonthlyAllocation = async () => {
    try {
      // First try to get monthly override
      const monthlyResponse = await adminApi.getProfitShareAllocation(selectedMonth, selectedYear);
      console.log('Monthly allocation response:', monthlyResponse.data);
      
      if (monthlyResponse.data.success && monthlyResponse.data.data) {
        // Has monthly override
        const newPercentages = {};
        const allocs = monthlyResponse.data.data.allocations || [];
        allocs.forEach(alloc => {
          const userId = alloc.recipient?._id || alloc.recipient;
          newPercentages[userId] = alloc.percentage;
        });
        setPercentages(newPercentages);
        setUsingDefault(false);
        setHasMonthlyOverride(true);
        return;
      }
    } catch (error) {
      console.log('No monthly override, loading default');
    }

    // No monthly override, load default
    try {
      const defaultResponse = await adminApi.getDefaultProfitShareAllocations();
      console.log('Default allocation response:', defaultResponse.data);
      
      if (defaultResponse.data.success && defaultResponse.data.data && defaultResponse.data.data.length > 0) {
        const newPercentages = {};
        defaultResponse.data.data.forEach(alloc => {
          const userId = alloc.profitSharer?._id || alloc.profitSharer;
          newPercentages[userId] = alloc.percentage;
        });
        setPercentages(newPercentages);
        setUsingDefault(true);
        setHasMonthlyOverride(false);
      } else {
        initializeEqualSplit();
        setUsingDefault(true);
        setHasMonthlyOverride(false);
      }
    } catch (error) {
      console.error('Error loading default:', error);
      initializeEqualSplit();
      setUsingDefault(true);
      setHasMonthlyOverride(false);
    }
  };

  const initializeEqualSplit = () => {
    if (profitSharers && profitSharers.length > 0) {
      const percentage = 100 / profitSharers.length;
      const newPercentages = {};
      profitSharers.forEach(ps => {
        newPercentages[ps._id] = parseFloat(percentage.toFixed(2));
      });
      setPercentages(newPercentages);
    }
  };

  const handlePercentageChange = (userId, value) => {
    if (mode === 'monthly') {
      setUsingDefault(false); // User is creating an override
    }
    setPercentages(prev => ({
      ...prev,
      [userId]: parseFloat(value) || 0
    }));
  };

  const saveAllocations = async () => {
    // Validate total is 100%
    const total = Object.values(percentages).reduce((sum, p) => sum + p, 0);
    if (Math.abs(total - 100) > 0.01) {
      setError(`Total percentage must equal 100% (currently ${total.toFixed(2)}%)`);
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (mode === 'admin') {
        // Save as default
        const allocations = profitSharers.map(ps => ({
          profitSharer: ps._id,
          percentage: percentages[ps._id] || 0
        }));
        await adminApi.saveDefaultProfitShareAllocations(allocations);
        setSuccessMessage('Default profit sharing saved successfully. This applies to all months unless overridden.');
        setTimeout(() => {
          setSuccessMessage('');
          onClose();
        }, 2000);
      } else {
        // Save as monthly override
        const allocations = profitSharers.map(ps => ({
          recipient: ps._id,
          recipientName: ps.name,
          percentage: percentages[ps._id] || 0
        }));
        await adminApi.saveProfitShareAllocation(selectedMonth, selectedYear, allocations);
        setSuccessMessage(`Custom allocation for ${getMonthName(selectedMonth)} ${selectedYear} saved successfully`);
        setHasMonthlyOverride(true);
        setUsingDefault(false);
        setTimeout(() => {
          setSuccessMessage('');
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving allocations:', error);
      setError(error.response?.data?.message || 'Failed to save profit sharing allocation');
    } finally {
      setLoading(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm(`Remove custom allocation for ${getMonthName(selectedMonth)} ${selectedYear} and use default?`)) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await adminApi.deleteProfitShareAllocation(selectedMonth, selectedYear);
      setSuccessMessage('Custom allocation removed. Now using default.');
      
      // Reload default
      await loadDefaultAllocation();
      setUsingDefault(true);
      setHasMonthlyOverride(false);
      
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (error) {
      console.error('Error resetting allocation:', error);
      if (error.response?.status === 404) {
        setSuccessMessage('Already using default allocation');
        await loadDefaultAllocation();
        setUsingDefault(true);
        setHasMonthlyOverride(false);
        setTimeout(() => setSuccessMessage(''), 2000);
      } else {
        setError(error.response?.data?.message || 'Failed to reset allocation');
      }
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (month) => {
    return new Date(2024, month - 1).toLocaleDateString('en-SG', { month: 'long' });
  };

  const getTotalPercentage = () => {
    return Object.values(percentages).reduce((sum, p) => sum + (p || 0), 0);
  };

  if (!isOpen) return null;

  // Show message if no profit sharers
  if (!profitSharers || profitSharers.length === 0) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content profit-sharing-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Manage Profit Sharing</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <p className="section-description">
              No profit sharers (co-founders) found. Please ensure tutors are marked as co-founders in the Admin Tutors page.
            </p>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content profit-sharing-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{mode === 'admin' ? 'Set Default Profit Sharing' : 'Manage Profit Sharing'}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && <ErrorMessage message={error} />}
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        <div className="modal-body">
          {mode === 'monthly' && (
            <>
              <div className="month-selector">
                <label>Period:</label>
                <select value={selectedMonth} disabled>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {getMonthName(i + 1)}
                    </option>
                  ))}
                </select>
                <select value={selectedYear} disabled>
                  {Array.from({ length: 6 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return <option key={year} value={year}>{year}</option>;
                  })}
                </select>
                {usingDefault && (
                  <span className="allocation-status-default">Using Default</span>
                )}
                {!usingDefault && hasMonthlyOverride && (
                  <span className="allocation-status-override">Custom Override</span>
                )}
              </div>

              <p className="section-description">
                {usingDefault 
                  ? `Currently using default allocation. Modify percentages below to create a custom override for ${getMonthName(selectedMonth)} ${selectedYear}.`
                  : `Custom allocation for ${getMonthName(selectedMonth)} ${selectedYear}. Click "Reset to Default" to remove this override.`
                }
              </p>
            </>
          )}

          {mode === 'admin' && (
            <p className="section-description">
              Set the default profit sharing percentages. This will apply to all months unless a specific month has a custom override.
            </p>
          )}

          {loading && <LoadingSpinner />}

          {!loading && (
            <>
              <table className="allocation-table">
                <thead>
                  <tr>
                    <th>Profit Sharer</th>
                    <th>Percentage (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {profitSharers.map(ps => (
                    <tr key={ps._id}>
                      <td>{ps.name}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={percentages[ps._id] || 0}
                          onChange={(e) => handlePercentageChange(ps._id, e.target.value)}
                          className="percentage-input"
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td>
                      <strong className={getTotalPercentage() === 100 ? 'valid-total' : 'invalid-total'}>
                        {getTotalPercentage().toFixed(2)}%
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={onClose}>Cancel</button>
                {mode === 'monthly' && hasMonthlyOverride && (
                  <button 
                    className="btn-reset" 
                    onClick={resetToDefault}
                    disabled={loading}
                  >
                    Reset to Default
                  </button>
                )}
                <button 
                  className="btn-primary" 
                  onClick={saveAllocations}
                  disabled={loading || getTotalPercentage() !== 100}
                >
                  {mode === 'admin' ? 'Save as Default' : (usingDefault ? 'Save as Override' : 'Save Override')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfitSharingModal;
